import { ONE } from "features/individuals/individual-details/consts";
import { FIRST_INDEX } from "features/individuals/individuals/individuals-list-table/const";

import { INDEX_OFFSET, ZERO } from "./constants";

const SLICE_START_INDEX = 1;
const CHARACTER_POSITION_INDEX = 0;
const TWELVE_HOUR = 12;
const TWENTY_THREE = 23;
const FIRST_DATE_ENDING_WITH_ST = 1;
// const DATE_ENDING_WITH_ST = 21;
const UPPER_LIMIT_DATE_ENDING_WITH_TH = 20;
const LOWER_LIMIT_DATE_ENDING_WITH_TH = 4;
const DATE_ENDING_WITH_RD = 3;
const DATE_ENDING_WITH_ND = 2;
const START_OF_MULTI_DIGIT_DATES = 10;
const MINUTES_PADDING = 2;
const MIN_DAY = 1;
const MAX_DAY = 31;
const DAY_ONE = 1;
const DAY_TWO = 2;
const DAY_THREE = 3;
const DAY_ELEVEN = 11;
const DAY_THIRTEEN = 13;
const TWO_DP_DIVISOR = 100;
const MILLI_SECONDS = 1000;

export const formatPathNameIntoReadableFormat = (route: string): string => {
  return route
    .split("-")
    .map(
      (word) =>
        word.charAt(CHARACTER_POSITION_INDEX).toUpperCase() +
        word.slice(SLICE_START_INDEX)
    )
    .join(" ");
};

export const formatLastPathNameIntoAReadableFormat = (
  route: string
): string => {
  const lastSegment = route.split("/").filter(Boolean).pop();

  if (!lastSegment) return "";
  return formatPathNameIntoReadableFormat(lastSegment);
};

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const getOrdinalSuffix = (day: number) => {
  if (
    day >= LOWER_LIMIT_DATE_ENDING_WITH_TH &&
    day <= UPPER_LIMIT_DATE_ENDING_WITH_TH
  )
    return "th";
  switch (day % START_OF_MULTI_DIGIT_DATES) {
    case FIRST_DATE_ENDING_WITH_ST:
      return "st";
    case DATE_ENDING_WITH_ND:
      return "nd";
    case DATE_ENDING_WITH_RD:
      return "rd";
    default:
      return "th";
  }
};

export const formatDateToDDMMTTSS = (dateString: string): string => {
  const date = new Date(dateString); // Create Date object
  const day = date.getDate(); // Use local date
  const month = monthNames[date.getMonth()]; // Use local month
  const hours = date.getHours(); // Use local time
  const minutes = date.getMinutes();

  // Determine AM/PM
  const period = hours >= TWELVE_HOUR ? "PM" : "AM";
  const formattedHours = hours % TWELVE_HOUR || TWELVE_HOUR;
  const formattedMinutes = minutes.toString().padStart(MINUTES_PADDING, "0");

  return `${day}${getOrdinalSuffix(day)} ${month}, ${formattedHours}:${formattedMinutes} ${period}`;
};

export const formatDateToDDMMTTssSS = (dateString: string): string => {
  const date = new Date(dateString); // Create Date object
  const day = date.getDate(); // Use local date
  const month = monthNames[date.getMonth()]; // Use local month
  const hours = date.getHours(); // Use local time
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  // Determine AM/PM
  const period = hours >= TWELVE_HOUR ? "PM" : "AM";
  const formattedHours = hours % TWELVE_HOUR || TWELVE_HOUR;
  const formattedMinutes = minutes.toString().padStart(MINUTES_PADDING, "0");
  const formattedSeconds = seconds.toString().padStart(MINUTES_PADDING, "0");

  return `${day}${getOrdinalSuffix(day)} ${month}, ${formattedHours}:${formattedMinutes}:${formattedSeconds} ${period}`;
};

export const getSecondsApart = (
  date1: string | Date,
  date2: string | Date
): string => {
  // Ensure both inputs are Date objects
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  // Check if dates are valid
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
    throw new Error("Invalid date format");
  }

  // Calculate the difference in milliseconds and convert to seconds
  const seconds = Math.abs((d2.getTime() - d1.getTime()) / MILLI_SECONDS);
  return `${Math.round(seconds * TWO_DP_DIVISOR) / TWO_DP_DIVISOR} seconds`;
};

// const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// const TWELVE_HOUR = 12;
// const MINUTES_PADDING = 2;

export const formatDateToDDMMHHMM = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= TWELVE_HOUR ? "PM" : "AM";
  const formattedHours = hours % TWELVE_HOUR || TWELVE_HOUR;
  const formattedMinutes = minutes.toString().padStart(MINUTES_PADDING, "0");

  return `${day}${getOrdinalSuffix(day)} ${month}, ${formattedHours}:${formattedMinutes}${period}`;
};

export const formatDateToDDMMYYTTSS = (dateString: string): string => {
  const date = new Date(dateString); // Create Date object
  const day = date.getDate(); // Use local date
  const month = monthNames[date.getMonth()]; // Use local month
  const year = date.getFullYear(); // Extract the year
  const hours = date.getHours(); // Use local time
  const minutes = date.getMinutes();

  const period = hours >= TWELVE_HOUR ? "pm" : "am";
  const formattedHours = hours % TWELVE_HOUR || TWELVE_HOUR;
  const formattedMinutes = minutes.toString().padStart(MINUTES_PADDING, "0");

  return `${day}${getOrdinalSuffix(day)} ${month}, ${year} at ${formattedHours}:${formattedMinutes}${period}`;
};

const MONTH_OFFSET = 1;
const MAX_LENGTH_MONTH = 2;
export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + MONTH_OFFSET).padStart(
    MAX_LENGTH_MONTH,
    "0"
  );
  const day = String(date.getDate()).padStart(MAX_LENGTH_MONTH, "0");
  return `${year}-${month}-${day}`;
};

export const formatDateToDDMMYYYY = (date: Date): string => {
  const day = String(date.getDate()).padStart(MAX_LENGTH_MONTH, "0");
  const month = String(date.getMonth() + INDEX_OFFSET).padStart(
    MAX_LENGTH_MONTH,
    "0"
  );
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// export const formatDateToDDMMYYYY = (date: string | Date): string => {
//   const parsedDate = typeof date === "string" ? new Date(date) : date;

//   if (isNaN(parsedDate.getTime())) {
//     throw new Error("Invalid date format");
//   }

//   const day = String(parsedDate.getDate()).padStart(MAX_LENGTH_MONTH, "0");
//   const month = String(parsedDate.getMonth() + INDEX_OFFSET).padStart(
//     MAX_LENGTH_MONTH,
//     "0"
//   );
//   const year = parsedDate.getFullYear();

//   return `${day}-${month}-${year}`;
// };

export const formatDDMMYYYYToDate = (dateString: string): Date => {
  const [day, month, year] = dateString.split("-").map(Number);
  return new Date(year, month - MIN_DAY, day);
};

const DECIMAL_START = 0;
const DECIMAL_END = 2;
export interface IHandleAmountFormatPayload {
  value: string;
  includeNairaSymbol?: boolean;
  shouldFormatZero?: boolean;
  isFormattedAmtNotPadded?: boolean;
  isFormattedWithDecimal?: boolean;
}
export const handleAmountFormat = (payload: IHandleAmountFormatPayload) => {
  const {
    value,
    includeNairaSymbol,
    shouldFormatZero,
    isFormattedAmtNotPadded,
    isFormattedWithDecimal,
  } = payload;

  if (value === "0" || !value) {
    let finalValue = "";
    if (shouldFormatZero && includeNairaSymbol) {
      finalValue = "₦0.00";
    } else if (shouldFormatZero && !includeNairaSymbol) {
      finalValue = "0";
    }
    return finalValue;
  }

  const cleanedValue = value.replace(/[^\d.]/g, "");
  const [integerPart, decimalPart] = cleanedValue.split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  let formattedValue = formattedInteger;

  if (decimalPart !== undefined) {
    formattedValue += `.${decimalPart.slice(DECIMAL_START, DECIMAL_END)}`; // Ensuring two decimal places
  } else if (isFormattedWithDecimal) {
    formattedValue += ".00"; // Append ".00" if decimal is required
  }

  if (includeNairaSymbol) {
    return `₦${isFormattedAmtNotPadded ? "" : " "}${formattedValue}`;
  }

  return formattedValue;
};

export const cleanCurrencyString = (value: string) => {
  return value.replace(/[₦,]/g, "");
};

export const capitalizeName = (
  firstName: string | null,
  lastName: string | null
): string => {
  const capitalize = (name: string | null): string =>
    name
      ? name.charAt(FIRST_INDEX).toUpperCase() + name.slice(ONE).toLowerCase()
      : "";

  const formattedFirstName = capitalize(firstName);
  const formattedLastName = capitalize(lastName);

  return `${formattedFirstName} ${formattedLastName}`.trim();
};

export const capitalizeWord = (word: string | null): string => {
  return word
    ? word.charAt(FIRST_INDEX).toUpperCase() + word.slice(ONE).toLowerCase()
    : "";
};

export const capitalizeSentence = (sentence: string) =>
  typeof sentence === "string"
    ? sentence
        .split(" ")
        .map((word) => word.charAt(ZERO).toUpperCase() + word.slice(ONE))
        .join(" ")
    : "";
export const formatTimeOfDay = (timeOfDay: number): string => {
  if (
    typeof timeOfDay !== "number" ||
    timeOfDay < ZERO ||
    timeOfDay > TWENTY_THREE
  ) {
    return "Invalid time";
  }

  const period = timeOfDay >= TWELVE_HOUR ? "PM" : "AM";
  const hours = timeOfDay % TWELVE_HOUR || TWELVE_HOUR; // Convert 0 to 12 for midnight, and 12 stays as 12 for noon

  return `${hours}:00${period}`;
};

export const formatDateToDDMMMYYYY = (
  dateString: string,
  isLongMonth?: boolean
): string => {
  const date = new Date(dateString);

  const day = date.getDate();
  const month = date.toLocaleString("en-US", {
    month: isLongMonth ? "long" : "short",
  });
  const year = date.getFullYear();
  return `${day}${getOrdinalSuffix(day)} ${month}, ${year}`;
};

export const formatDateTo12HourTime = (isoString: string) => {
  const date = new Date(isoString);
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= TWELVE_HOUR ? "PM" : "AM";

  hours = hours % TWELVE_HOUR || TWELVE_HOUR; // Convert 0 (midnight) to 12

  return `${hours}:${minutes.toString().padStart(MINUTES_PADDING, "0")} ${ampm}`;
};

export const formatDayWithOrdinals = (day: number) => {
  if (day < MIN_DAY || day > MAX_DAY) {
    throw new Error("Day must be between 1 and 31");
  }

  const suffixes: Record<number, string> = {
    [DAY_ONE]: "st",
    [DAY_TWO]: "nd",
    [DAY_THREE]: "rd",
  };
  const remainder = day % START_OF_MULTI_DIGIT_DATES;
  const suffix =
    day >= DAY_ELEVEN && day <= DAY_THIRTEEN
      ? "th"
      : suffixes[remainder] || "th";

  return `${day}${suffix}`;
};

export const getFormattedDayFromDateString = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getUTCDate(); // Extract the day in UTC
  return formatDayWithOrdinals(day);
};

export const formatDateToMMYYYY = (dateString: string): string => {
  const date = new Date(dateString);

  const month = date.toLocaleString("en-US", {
    month: "long",
  });
  const year = date.getFullYear();
  return `${month}, ${year}`;
};

export const handleTruncateAmount = (value: number): number => {
  return Math.trunc(value);
};

export const getFormattedDateTimeParts = (
  dateString: string
): {
  datePart: string;
  timePart: string;
} => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= TWELVE_HOUR ? "PM" : "AM";
  const formattedHours = hours % TWELVE_HOUR || TWELVE_HOUR;
  const formattedMinutes = minutes.toString().padStart(MINUTES_PADDING, "0");

  return {
    datePart: `${day} ${month}, ${year}`,
    timePart: `${formattedHours}:${formattedMinutes} ${period}`,
  };
};

const DAYS_IN_A_MONTH = 30;
export const convertDaysToMonthsAndDays = (days: number): string => {
  const months = Math.floor(days / DAYS_IN_A_MONTH);
  const remainingDays = days % DAYS_IN_A_MONTH;

  const monthStr = months === ONE ? "1 month" : `${months} months`;
  const dayStr = remainingDays === ONE ? "1 day" : `${remainingDays} days`;

  if (months > ZERO && remainingDays > ZERO) {
    return `${monthStr} ${dayStr}`;
  }
  if (months > ZERO) return monthStr;
  return dayStr;
};
